import { create } from 'zustand';
import {
  createJSONStorage,
  devtools,
  persist,
  type StateStorage,
} from 'zustand/middleware';

import type { User } from '@revelio/data-access';

import {
  DEFAULT_IMPERSONATION_STATE,
  type ImpersonationState,
  type UserOverrides,
} from './impersonation.model';

const STORAGE_KEY = 'rl_impersonation';

export type ImpersonationStore = {
  state: ImpersonationState;
  actions: {
    startImpersonation: (user: Partial<User>) => void;
    setOverrides: (overrides: UserOverrides) => void;
    clearImpersonation: () => void;
  };
};

type PersistedImpersonationStore = {
  state: ImpersonationState;
};

const sessionStorageAdapter: StateStorage = {
  getItem: (name) => {
    if (typeof window === 'undefined') {
      return null;
    }
    return sessionStorage.getItem(name);
  },
  setItem: (name, value) => {
    if (typeof window === 'undefined') {
      return;
    }
    sessionStorage.setItem(name, value);
  },
  removeItem: (name) => {
    if (typeof window === 'undefined') {
      return;
    }
    sessionStorage.removeItem(name);
  },
};

const mergePersistedState = (
  persistedState: unknown,
  currentState: ImpersonationStore
): ImpersonationStore => {
  const persisted = persistedState as Partial<PersistedImpersonationStore>;
  return {
    ...currentState,
    state: {
      ...currentState.state,
      ...(persisted.state ?? {}),
    },
  };
};

export const useImpersonationStoreBase = create<ImpersonationStore>()(
  devtools(
    persist(
      (set) => ({
        state: { ...DEFAULT_IMPERSONATION_STATE },
        actions: {
          startImpersonation: (user) =>
            set((current) => ({
              state: {
                ...current.state,
                isImpersonating: true,
                impersonatedUserId: user.id || null,
                impersonatedUser: user,
                overrides: null,
              },
            })),
          setOverrides: (overrides) =>
            set((current) => ({
              state: {
                ...current.state,
                isImpersonating: false,
                impersonatedUserId: null,
                impersonatedUser: null,
                overrides,
              },
            })),
          clearImpersonation: () =>
            set(() => ({
              state: { ...DEFAULT_IMPERSONATION_STATE },
            })),
        },
      }),
      {
        name: STORAGE_KEY,
        storage: createJSONStorage<PersistedImpersonationStore>(
          () => sessionStorageAdapter
        ),
        partialize: (store) => ({ state: store.state }),
        merge: mergePersistedState,
      }
    ),
    { name: 'ImpersonationStore' }
  )
);

export const getImpersonationStateSnapshot = () =>
  useImpersonationStoreBase.getState().state;
